import React, { useState, useRef, useEffect } from 'react';

/**
 * Custom Date Input Component
 * - Separate fields for month, day, year
 * - Auto-advances cursor between fields
 * - Better year entry support
 */
export function DateInput({ value, onChange, required = false, className = '', placeholder = 'MM/DD/YYYY', 'data-testid': testId = 'date-input' }) {
  const [month, setMonth] = useState('');
  const [day, setDay] = useState('');
  const [year, setYear] = useState('');
  
  const monthRef = useRef(null);
  const dayRef = useRef(null);
  const yearRef = useRef(null);

  // Parse initial value (YYYY-MM-DD format)
  useEffect(() => {
    if (value) {
      const parts = value.split('-');
      if (parts.length === 3) {
        setYear(parts[0]);
        setMonth(parts[1]);
        setDay(parts[2]);
      }
    }
  }, []);

  // Update parent when values change
  const updateValue = (m, d, y) => {
    if (m && d && y && y.length === 4) {
      const formattedDate = `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
      onChange(formattedDate);
    } else if (!m && !d && !y) {
      onChange('');
    }
  };

  const handleMonthChange = (e) => {
    let val = e.target.value.replace(/\D/g, '').slice(0, 2);
    
    // Auto-correct invalid months
    if (val.length === 2) {
      const num = parseInt(val);
      if (num > 12) val = '12';
      if (num < 1 && val !== '0') val = '01';
    }
    
    setMonth(val);
    updateValue(val, day, year);
    
    // Auto-advance to day field
    if (val.length === 2) {
      dayRef.current?.focus();
    }
  };

  const handleDayChange = (e) => {
    let val = e.target.value.replace(/\D/g, '').slice(0, 2);
    
    // Auto-correct invalid days
    if (val.length === 2) {
      const num = parseInt(val);
      if (num > 31) val = '31';
      if (num < 1 && val !== '0') val = '01';
    }
    
    setDay(val);
    updateValue(month, val, year);
    
    // Auto-advance to year field
    if (val.length === 2) {
      yearRef.current?.focus();
    }
  };

  const handleYearChange = (e) => {
    let val = e.target.value.replace(/\D/g, '').slice(0, 4);
    setYear(val);
    updateValue(month, day, val);
  };

  const handleMonthKeyDown = (e) => {
    if (e.key === 'Backspace' && month === '') {
      // Stay in month field
    }
  };

  const handleDayKeyDown = (e) => {
    if (e.key === 'Backspace' && day === '') {
      monthRef.current?.focus();
    }
  };

  const handleYearKeyDown = (e) => {
    if (e.key === 'Backspace' && year === '') {
      dayRef.current?.focus();
    }
  };

  const baseInputClass = "w-full text-center bg-transparent outline-none";

  return (
    <div className={`flex items-center border rounded-lg px-3 py-2 ${className}`}>
      <input
        ref={monthRef}
        type="text"
        value={month}
        onChange={handleMonthChange}
        onKeyDown={handleMonthKeyDown}
        placeholder="MM"
        className={`${baseInputClass} w-8`}
        maxLength={2}
        required={required}
        data-testid={`${testId}-month`}
      />
      <span className="text-gray-400 mx-1">/</span>
      <input
        ref={dayRef}
        type="text"
        value={day}
        onChange={handleDayChange}
        onKeyDown={handleDayKeyDown}
        placeholder="DD"
        className={`${baseInputClass} w-8`}
        maxLength={2}
        required={required}
        data-testid={`${testId}-day`}
      />
      <span className="text-gray-400 mx-1">/</span>
      <input
        ref={yearRef}
        type="text"
        value={year}
        onChange={handleYearChange}
        onKeyDown={handleYearKeyDown}
        placeholder="YYYY"
        className={`${baseInputClass} w-12`}
        maxLength={4}
        required={required}
        data-testid={`${testId}-year`}
      />
    </div>
  );
}

export default DateInput;
